/**
 * The Dev-only QA panel (Launch Board L-36; ADR-77; plans/PKG-A.md A4).
 *
 * Testers walk a checklist on Dev and mark each item Done, Problem, Question,
 * Confusing or Blocked, with a note and the page they were on. `waitingOn` is
 * allowed only with Blocked (Matt's QA format decision; the database holds the
 * same rule as a CHECK). The API answers only when APP_ENV is test.
 */

import { z } from 'zod';

export const QA_STATUSES = ['done', 'problem', 'question', 'confusing', 'blocked'] as const;
export type QaStatus = (typeof QA_STATUSES)[number];

/** The words a tester sees. */
export const QA_STATUS_LABEL: Record<QaStatus, string> = {
  done: 'Done',
  problem: 'Problem',
  question: 'Question',
  confusing: 'Confusing',
  blocked: 'Blocked',
};

export interface QaChecklistItem {
  readonly id: string;
  readonly scriptId: string;
  readonly groupLabel: string | null;
  readonly screen: string | null;
  readonly stepText: string | null;
  readonly roleHint: string | null;
  readonly expectedResult: string | null;
}

export interface QaFeedback {
  readonly id: string;
  readonly itemId: string | null;
  readonly status: QaStatus;
  readonly waitingOn: string | null;
  readonly note: string | null;
  readonly screenUrl: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly version: number;
}

/** A row on /admin/qa: the feedback, who left it, and what it was about. */
export interface QaFeedbackReviewRow extends QaFeedback {
  readonly authorUserId: string;
  readonly authorName: string;
  readonly scriptId: string | null;
  readonly groupLabel: string | null;
  readonly screen: string | null;
}

const waitingOnRule = (value: { status: QaStatus; waitingOn?: string | null | undefined }) =>
  value.status === 'blocked' || value.waitingOn === null || value.waitingOn === undefined;
const waitingOnIssue = {
  message: 'Say what you are waiting on only when the status is Blocked',
  path: ['waitingOn'],
};

const feedbackFields = {
  status: z.enum(QA_STATUSES),
  waitingOn: z.string().trim().min(1).max(500).nullable().optional(),
  note: z.string().trim().max(4000).nullable().optional(),
  screenUrl: z.string().trim().max(2000).nullable().optional(),
};

/** POST /api/v1/qa/feedback. */
export const qaFeedbackCreateSchema = z
  .object({ itemId: z.uuid().nullable(), ...feedbackFields })
  .refine(waitingOnRule, waitingOnIssue);
export type QaFeedbackCreate = z.infer<typeof qaFeedbackCreateSchema>;

/**
 * PATCH /api/v1/qa/feedback/:id — replaces the answer, version-checked. The
 * object is declared on its own so the write-surface gate can read it and hold
 * the concurrency token in it (docs/11 F-49).
 */
const qaFeedbackWriteShape = z.object({
  version: z.number().int().positive(),
  ...feedbackFields,
});
export const qaFeedbackWriteSchema = qaFeedbackWriteShape.refine(waitingOnRule, waitingOnIssue);
export type QaFeedbackWrite = z.infer<typeof qaFeedbackWriteSchema>;

/** GET /api/v1/qa/feedback and feedback.csv: the review filters. */
export const qaFeedbackFilterSchema = z.object({
  author: z.uuid().optional(),
  status: z.enum(QA_STATUSES).optional(),
  screen: z.string().trim().min(1).max(200).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type QaFeedbackFilter = z.infer<typeof qaFeedbackFilterSchema>;
